/**
 * Approximate workload (hours) on project tasks.
 *
 * The column is optional on purpose: empty means "not estimated yet",
 * never zero. Values are planning estimates so a day or week of due
 * work can be checked against a realistic capacity — they are not
 * timesheet actuals.
 */

/** Typical booked hours in a single workday. Days above this are flagged. */
export const WORKDAY_HOURS = 8;

/** Typical booked hours in a Monday–Sunday work week. */
export const WORKWEEK_HOURS = 40;

/**
 * Postgres `numeric` often arrives as a string. Collapse that (and
 * junk / non-positive values) to `number | null` so the rest of the
 * UI can treat the field as a real number.
 */
export function coerceWorkloadHours(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

/** Display form: `4h`, `1.5h`. Empty string when unset. */
export function formatWorkloadHours(value: unknown): string {
  const n = coerceWorkloadHours(value);
  if (n == null) return '';
  return `${Number(n.toFixed(2))}h`;
}

/**
 * Parse a free-text hours field (`"4"`, `"1.5"`, `"8h"`). Blank →
 * null (clear the estimate). Throws on a malformed or non-positive
 * value so the caller can toast.
 */
export function parseWorkloadHours(raw: string): number | null {
  const trimmed = raw.trim().replace(/h$/i, '').trim();
  if (!trimmed) return null;
  const n = Number.parseFloat(trimmed);
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error('Workload must be a positive number of hours.');
  }
  if (n > 9999.99) {
    throw new Error('Workload can’t exceed 9,999.99 hours.');
  }
  return Math.round(n * 100) / 100;
}

export function isHeavyDay(hours: number): boolean {
  return hours > WORKDAY_HOURS;
}

export function isHeavyWeek(hours: number): boolean {
  return hours > WORKWEEK_HOURS;
}

function pad2(n: number): string {
  return `${n}`.padStart(2, '0');
}

/** Local `YYYY-MM-DD` — matches `tasks.due_date` (a date column). */
export function localDateKey(d: Date = new Date()): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function addLocalDays(d: Date, n: number): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() + n);
  return out;
}

/** Monday of the local week containing `d` (ISO-style, week starts Monday). */
export function startOfLocalWeekMonday(d: Date = new Date()): Date {
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? -6 : 1 - day;
  return addLocalDays(d, diff);
}

export function endOfLocalWeekSunday(d: Date = new Date()): Date {
  return addLocalDays(startOfLocalWeekMonday(d), 6);
}

export function sumWorkloadHours(
  items: Array<{ approximate_workload_hours?: unknown; is_completed?: boolean }>,
  opts?: { includeCompleted?: boolean },
): number {
  let total = 0;
  for (const item of items) {
    if (!opts?.includeCompleted && item.is_completed) continue;
    total += coerceWorkloadHours(item.approximate_workload_hours) ?? 0;
  }
  return Math.round(total * 100) / 100;
}
